import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { libraryMembersApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type { LibraryMember, MemberStatus, MemberType } from '@/types/library';
import { formatDate, orDash } from '@/utils/format';
import { MemberStatusBadge, Money, humanise } from './shared';

const STATUSES: { value: MemberStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Can borrow' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'EXPIRED', label: 'Lapsed' },
  { value: 'CLOSED', label: 'Closed' },
];

const TYPES: { value: MemberType; label: string }[] = [
  { value: 'STUDENT', label: 'Students' },
  { value: 'TEACHER', label: 'Teachers' },
  { value: 'STAFF', label: 'Other staff' },
];

/**
 * Who may borrow.
 *
 * <p>A membership is a thin record pointing at a student or an employee — never
 * a second copy of their name and class. When somebody leaves, their record in
 * the student or staff module changes and the library follows, rather than
 * quietly keeping a stale copy that says otherwise.
 *
 * <p>Suspending is not deleting. A closed membership keeps its borrowing history,
 * because "what did this child read in Year 4" is a question schools actually
 * ask.
 */
export function LibraryMembersPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('membershipNumber,asc');
  const manages = can(PERMISSIONS.LIBRARY_MEMBER_MANAGE);

  const [status, setStatus] = useState<MemberStatus | ''>('');
  const [memberType, setMemberType] = useState<MemberType | ''>('');

  const params = {
    ...table.params,
    ...(status === '' ? {} : { status }),
    ...(memberType === '' ? {} : { memberType }),
  };

  const members = useQuery({
    queryKey: queryKeys.libraryMembers.list(params),
    queryFn: () => libraryMembersApi.list(params),
  });

  const setMemberStatus = useMutation({
    mutationFn: ({ id, next, why }: { id: string; next: MemberStatus; why: string }) =>
      libraryMembersApi.setStatus(id, next, why),
    onSuccess: (member) => {
      toast.success(`${member.membershipNumber} is now ${humanise(member.status)}`);
      void queryClient.invalidateQueries({ queryKey: queryKeys.libraryMembers.all });
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<LibraryMember>[] = [
    {
      key: 'membershipNumber',
      header: 'Member',
      sortKey: 'membershipNumber',
      render: (row) => (
        <span>
          <Link
            to={ROUTES.libraryMemberDetail(row.id)}
            className="block text-sm font-semibold text-ink underline-offset-2 hover:text-accent hover:underline"
          >
            {row.name}
          </Link>
          <span className="block font-mono text-[11px] text-subtle">{row.membershipNumber}</span>
        </span>
      ),
    },
    { key: 'memberType', header: 'Type', render: (row) => humanise(row.memberType) },
    {
      key: 'reference',
      header: 'School number',
      render: (row) => (
        <span className="font-mono text-[12px] text-muted">
          {orDash(row.studentNumber ?? row.employeeNumber)}
        </span>
      ),
    },
    {
      key: 'booksOut',
      header: 'Out',
      className: 'text-right',
      render: (row) => (
        <span>
          <span className="text-sm text-ink">
            {row.booksOut} of {row.borrowingLimit}
          </span>
          {row.limitIsOverridden && (
            <span className="block text-[11px] text-subtle">limit raised for them</span>
          )}
        </span>
      ),
    },
    {
      key: 'outstandingFines',
      header: 'Fines',
      className: 'text-right',
      render: (row) => <Money value={row.outstandingFines} />,
    },
    { key: 'registeredOn', header: 'Joined', render: (row) => formatDate(row.registeredOn) },
    { key: 'status', header: 'Status', render: (row) => <MemberStatusBadge status={row.status} /> },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        manages && (row.status === 'ACTIVE' || row.status === 'SUSPENDED') ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const next: MemberStatus = row.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
              const why = window.prompt(
                next === 'SUSPENDED'
                  ? 'Why is borrowing being suspended? Books already out stay out.'
                  : 'Why is borrowing being restored?',
              );
              if (why !== null && why.trim() !== '') {
                setMemberStatus.mutate({ id: row.id, next, why: why.trim() });
              }
            }}
          >
            {row.status === 'ACTIVE' ? 'Suspend' : 'Restore'}
          </Button>
        ) : null,
    },
  ];

  return (
    <ListPage
      title="Members"
      description="Students and staff who may borrow."
      icon={<Users className="h-5 w-5" aria-hidden="true" />}
      search={{
        value: table.searchInput,
        onChange: table.setSearchInput,
        placeholder: 'Name or membership number…',
      }}
      filters={
        <>
          <FilterSelect
            label="Type"
            value={memberType}
            onChange={(value) => {
              setMemberType(value);
              table.setPage(0);
            }}
            options={TYPES}
          />
          <FilterSelect
            label="Status"
            value={status}
            onChange={(value) => {
              setStatus(value);
              table.setPage(0);
            }}
            options={STATUSES}
          />
        </>
      }
      pagination={members.data?.pagination}
      onPageChange={table.setPage}
      onSizeChange={table.setSize}
    >
      <DataTable
        columns={columns}
        rows={members.data?.data}
        rowKey={(row) => row.id}
        loading={members.isPending}
        error={members.error}
        onRetry={() => void members.refetch()}
        sort={table.sort}
        onSort={table.toggleSort}
        emptyTitle="Nobody is enrolled"
        emptyDescription="A student or employee has to be given a library membership before they can borrow."
      />
    </ListPage>
  );
}
